import type {SocketData} from './signalr-socket'
import {SignalRSocket} from './signalr-socket'
import {StepState, bufferLogs, bufferedStepIds, expandStep, stepsMap, updateStreamingLogs} from './steps'
import {attr, controller} from '@github/catalyst'
import type {CheckStepElement} from './check-step'
import {fetchJson} from './fetch'
import {sendStats} from '@github-ui/stats'

const MAX_RETRIES = 5
const RETRY_DELAY = 2000

interface StepData {
  id: string
  name: string
  status: string
  conclusion?: string
  started_at?: string
  completed_at?: string
  change_id: number
  completed_log_url?: string
  number: number
  log_url?: string
  is_background?: boolean
  background_control_type?: string
  background_control_step_ids?: string[]
  parallel_group_id?: string
}

interface StepBackscrollLogs {
  lines: LogLine[]
}

interface LogLine {
  id: string
  line: string
}

interface AgentData {
  stepRecordId: string // uuid like external id
  lines: string[]
  startLine: number | null
}

// Incoming data over alive socket, see CheckRun.notify_socket_subscribers
interface AliveSocketData {
  timestamp: string
  reason: string
  wait: number
  status: 'requested' | 'queued' | 'in_progress' | 'completed' | 'waiting' | 'pending'
  has_steps: boolean
  raw_logs: boolean
  streaming_logs: boolean
}

interface ResultsLogLine {
  line: string
  lineID: string
}

interface StepUpdate {
  workflowRunBackendId: string
  workflowJobRunBackendId: string
  stepId: string
  stepNumber: number
  changeOrder: number
  status: number
}

interface ResultsData {
  lines: ResultsLogLine[]
  count: number
  startLine: number
  stepId: string
}

const AliveMessageKind = {
  StepLog: 0,
  StepsUpdate: 1,
} as const

type AliveMessageKind = (typeof AliveMessageKind)[keyof typeof AliveMessageKind]

const tsFromID = (id: string): Date => {
  const unixMs = parseInt(id.split('-')[0] || '')
  if (isNaN(unixMs)) return new Date()
  return new Date(unixMs)
}

@controller('check-steps')
class CheckStepsElement extends HTMLElement {
  @attr useActionsUx = false
  @attr loggedIn = false
  @attr jobStepsUrl = ''
  @attr jobStatus = ''
  @attr streamingUrl = ''
  @attr canStream = false
  @attr stepsFromResults = false
  #latestChangeId = 0
  #requestInProgressOrWaiting = false
  #retries = 0
  #abortController = new AbortController()
  #streamingLogSocket = SignalRSocket.getInstance('WatchRunAsync')
  #logIds = new Set<string>()
  #backscrollFetched = false
  #aliveBackscrollBuffer: Record<string, LogLine[]> = {}

  connectedCallback() {
    this.processParallelGroups()
    if (this.loggedIn) {
      this.addEventListener('socket:message', this.handleAliveEvent.bind(this))
      this.createOrCloseStreamingLogSocket()
      this.requestJobSteps(this.isJobCompleted())
    }
  }

  disconnectedCallback() {
    this.#abortController.abort()
    // cleanup alive listener
    this.removeEventListener('socket:message', this.handleAliveEvent.bind(this))
    // cleanup children
    this.textContent = ''
  }

  static observedAttributes = ['data-can-stream', 'data-job-status']
  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return

    switch (name) {
      case 'data-can-stream':
      case 'data-job-status':
        this.createOrCloseStreamingLogSocket()
        if (this.isJobCompleted()) {
          // there is a race condition on last step update via signalr socket vs. job complete over alive socket
          this.requestJobSteps(true)
        }
        break
      default:
      // Do nothing
    }
  }

  handleAliveEvent(event: Event) {
    const aliveData = (event as CustomEvent)?.detail
    if (!aliveData) return
    const channel = aliveData.name?.split(':')?.[0]
    switch (channel) {
      case 'check_runs': {
        const data = aliveData.data as AliveSocketData
        this.jobStatus = data.status
        this.canStream = data.streaming_logs
        break
      }
      case 'actions_results': {
        const messageKind = aliveData.data.kind
        if (messageKind === AliveMessageKind.StepLog) {
          const resultsData = aliveData.data.data as ResultsData
          this.handleAliveStepLogEvent(resultsData)
        } else if (messageKind === AliveMessageKind.StepsUpdate) {
          const stepsUpdate = aliveData.data.data as StepUpdate[]
          this.handleAliveStepUpdateEvent(stepsUpdate)
        }
        break
      }
      default:
      // Do nothing if no other matches
    }
  }

  handleAliveStepUpdateEvent(stepsUpdate: StepUpdate[]) {
    if (this.stepsFromResults) {
      const changeId = Math.min(...stepsUpdate.map(s => s.changeOrder))
      this.#latestChangeId = changeId
      this.requestJobSteps()
    }
  }

  handleAliveStepLogEvent(resultsData: ResultsData) {
    let updateJobSteps = false
    const step = stepsMap[resultsData.stepId]
    // dedup potential lines retrieved by backscrolls
    const newLogs = resultsData.lines.filter(l => !this.#logIds.has(l.lineID))
    const rawLines = newLogs.map(l => l.line)
    if (!step || !this.#backscrollFetched) {
      for (const l of newLogs) {
        this.#logIds.add(l.lineID)
      }
      this.#aliveBackscrollBuffer[resultsData.stepId] ||= []
      this.#aliveBackscrollBuffer[resultsData.stepId]?.push(...newLogs.map(({line, lineID: id}) => ({id, line})))
      updateJobSteps = true
    } else {
      const oldStepState = step.state
      updateStreamingLogs(step, rawLines, resultsData.startLine)
      updateJobSteps = oldStepState !== step.state
    }

    if (updateJobSteps) {
      this.requestJobSteps()
    }
  }

  createOrCloseStreamingLogSocket() {
    if (this.stepsFromResults) return
    if (!this.streamingUrl || !this.canStream) return

    const {signal} = this.#abortController

    if (this.jobStatus === 'completed') {
      // close socket (if exists) if the job is completed
      this.#abortController.abort()
      this.#streamingLogSocket.closeExistingSocket()
      return
    }

    this.requestJobSteps()
    this.#streamingLogSocket.startOrContinueStreaming(this.streamingUrl)

    this.#streamingLogSocket.removeEventListener('onStreamFailure', this.handleStreamFailure)
    this.#streamingLogSocket.addEventListener('onStreamFailure', this.handleStreamFailure, {signal})

    this.#streamingLogSocket.removeEventListener('socketDidReceiveMessage', this.handleSocketDidReceiveMessage)
    this.#streamingLogSocket.addEventListener('socketDidReceiveMessage', this.handleSocketDidReceiveMessage, {signal})
  }

  private handleStreamFailure = () => {
    sendStats({incrementKey: 'ACTIONS_STREAMING_LOG_VIEWS_FAIL'})
  }

  private handleSocketDidReceiveMessage = (event: Event) => {
    if (!(event instanceof CustomEvent)) return
    this.processSocketMessage(event.detail)
  }

  processSocketMessage(data: SocketData<AgentData[]>) {
    let updateJobSteps = false
    const logUpdates = Array.isArray(data.arguments) ? data.arguments : []

    for (const logUpdate of logUpdates) {
      const {lines, stepRecordId, startLine} = logUpdate
      const step = stepsMap[stepRecordId]
      if (!step) {
        bufferLogs(stepRecordId, lines)
        updateJobSteps = true
        continue
      }

      const oldStepState = step.state
      updateStreamingLogs(step, lines, startLine)
      updateJobSteps = oldStepState !== step.state
    }

    if (updateJobSteps) this.requestJobSteps()
  }

  async requestJobSteps(force = false) {
    if (this.#requestInProgressOrWaiting) return
    if (!this.jobStepsUrl) return
    if (!force && this.hasSteps() && this.areStepsCompleted()) return

    const requestedChangeId = this.#latestChangeId

    this.#requestInProgressOrWaiting = true
    const data = await fetchJson<StepData[]>(`${this.jobStepsUrl}?change_id=${this.#latestChangeId}`)
    this.#requestInProgressOrWaiting = false
    if (!data) {
      // req err, wait and retry
      const canRetry = await this.waitAndIncrementRetries()
      if (!canRetry) return
      this.requestJobSteps()
      return
    }

    // for refetching on missing data
    let dataMismatch = false

    // no data back for changeid
    if (!data.length) {
      dataMismatch = true
    }

    const ids = new Set(bufferedStepIds())
    for (const step of data) {
      const existing = stepsMap[step.id]
      if (!existing) {
        this.addStep(step)
      } else {
        const checkStep = existing.container.closest<CheckStepElement>('check-step')!
        this.updateStepAttributes(checkStep, step)
        checkStep.update()

        if (this.shouldAutoExpandInlineDetails(step)) {
          expandStep(existing)
        }
      }

      // corner case: a step is streaming without a started_at
      if (existing?.state === StepState.STREAMING && !step.started_at) {
        dataMismatch = true
      }

      // corner case: a concluded step without log url
      if (step.completed_at && !step.completed_log_url) {
        dataMismatch = true
      }

      this.#latestChangeId = Math.max(this.#latestChangeId, step.change_id)
      ids.delete(step.id)
    }

    if (!this.#backscrollFetched) {
      await this.fetchInitialBackscrolls()
      this.#backscrollFetched = true
    }

    this.flushBufferedLogs()
    this.processParallelGroups()

    // If there are buffered steps means we received additional logs from
    // unknown steps during the request.
    if (ids.size > 0 || dataMismatch) {
      if (this.#latestChangeId === requestedChangeId) {
        const canRetry = await this.waitAndIncrementRetries()
        if (!canRetry) return
      }
      this.requestJobSteps()
    } else {
      // if all ok, reset retries
      this.#retries = 0
    }
  }

  addStep(data: StepData) {
    const step = document.createElement('check-step')
    step.setAttribute('data-external-id', data.id)
    this.updateStepAttributes(step, data)
    this.appendChild(step)
    this.processParallelGroups()
  }

  shouldAutoExpandInlineDetails(data: StepData) {
    return (
      !!data.started_at &&
      !data.conclusion &&
      (data.background_control_type === 'wait' || data.background_control_type === 'wait-all')
    )
  }

  processParallelGroups() {
    const allSteps = [...this.querySelectorAll<CheckStepElement>('check-step')]
    const groupIds = new Set<string>()

    // Collect all parallel group IDs (skip empty strings from Catalyst defaults)
    for (const step of allSteps) {
      const groupId = step.getAttribute('data-parallel-group-id')
      if (groupId) groupIds.add(groupId)
    }

    for (const groupId of groupIds) {
      const existingHeader = this.querySelector<CheckStepElement>(
        `check-step[data-parallel-group-header][data-parallel-group-id="${groupId}"]`,
      )

      // Find child steps (background steps in this group)
      const childSteps = allSteps.filter(
        s =>
          s.getAttribute('data-parallel-group-id') === groupId &&
          s.hasAttribute('data-background') &&
          !s.getAttribute('data-step-type') &&
          !s.hasAttribute('data-parallel-group-header'),
      )

      if (childSteps.length === 0) continue

      // Derive group conclusion/progress from children
      const childConclusions = childSteps.map(s => s.getAttribute('data-conclusion') || '')
      const anyInProgress = childSteps.some(s => s.hasAttribute('data-in-progress'))
      const allCompleted = childConclusions.every(c => c && c !== 'null')
      const anyFailed = childConclusions.some(c => c === 'failure')
      const anyStarted = childSteps.some(s => s.getAttribute('data-started-at'))

      // Match regular step behavior: a group is expanded while not started/in progress and
      // stays expanded after completing within the same session. On first render (e.g. page
      // refresh) an already-completed group starts collapsed. For an existing header we keep
      // its current state so live runs and user toggles are never overridden on a poll.
      const isExpanded = existingHeader ? existingHeader.hasAttribute('data-parallel-group-expanded') : !allCompleted

      // Mark children as parallel group members, respecting the group's expanded state.
      // Visibility is toggled per-child via an attribute (not a CSS sibling combinator)
      // so expanding one group never reveals another group's children.
      for (const child of childSteps) {
        child.setAttribute('data-parallel-group-child', '')
        child.toggleAttribute('data-parallel-group-child-visible', isExpanded)
      }

      // Hide the wait step in this group — the group header replaces it
      const waitStep = allSteps.find(
        s =>
          s.getAttribute('data-parallel-group-id') === groupId &&
          (s.getAttribute('data-step-type') === 'wait' || s.getAttribute('data-step-type') === 'wait-all'),
      )
      if (waitStep) {
        waitStep.hidden = true
      }

      let groupConclusion = ''
      if (allCompleted) {
        groupConclusion = anyFailed ? 'failure' : 'success'
      }

      // Derive timing from the earliest start and latest end
      const startTimes = childSteps.map(s => s.getAttribute('data-started-at')).filter(Boolean) as string[]
      const endTimes = childSteps.map(s => s.getAttribute('data-completed-at')).filter(Boolean) as string[]
      const earliestStart = startTimes.length ? startTimes.sort()[0] : ''
      const latestEnd = allCompleted && endTimes.length ? endTimes.sort().reverse()[0] : ''

      if (existingHeader) {
        existingHeader.setAttribute('data-parallel-group-count', String(childSteps.length))
        existingHeader.setAttribute('data-conclusion', groupConclusion || 'null')
        existingHeader.setAttribute('data-started-at', earliestStart || '')
        existingHeader.setAttribute('data-completed-at', latestEnd || '')
        // Only manage in-progress state here; the expanded state is user-controlled
        // via toggleParallelGroup() and must not be overridden on every poll.
        if (anyInProgress || (anyStarted && !allCompleted)) {
          existingHeader.setAttribute('data-in-progress', '')
        } else {
          existingHeader.removeAttribute('data-in-progress')
        }
        existingHeader.update()
        continue
      }

      // eslint-disable-next-line i18n-text/no-en
      const displayName = 'Parallel group'

      // Create the group header — expanded unless the group is already complete on first render
      const header = document.createElement('check-step')
      header.setAttribute('data-parallel-group-header', '')
      header.setAttribute('data-parallel-group-id', groupId)
      header.setAttribute('data-parallel-group-count', String(childSteps.length))
      header.setAttribute('data-name', displayName)
      header.setAttribute('data-number', childSteps[0]?.getAttribute('data-number') || '0')
      header.setAttribute('data-conclusion', groupConclusion || 'null')
      header.setAttribute('data-started-at', earliestStart || '')
      header.setAttribute('data-completed-at', latestEnd || '')
      if (anyInProgress || (anyStarted && !allCompleted)) {
        header.setAttribute('data-in-progress', '')
      }
      if (isExpanded) {
        header.setAttribute('data-parallel-group-expanded', '')
      }
      if (this.useActionsUx) {
        header.setAttribute('data-use-actions-ux', '')
      }
      if (this.loggedIn) {
        header.setAttribute('data-logged-in', '')
      }
      if (this.isJobCompleted()) {
        header.setAttribute('data-job-completed', '')
      }

      // Insert header before the first child step
      childSteps[0]!.before(header)
    }
  }

  updateStepAttributes(step: Element, data: StepData) {
    step.setAttribute('data-name', data.name)
    step.setAttribute('data-number', String(data.number))
    step.setAttribute('data-started-at', data.started_at || '')
    step.setAttribute('data-completed-at', data.completed_at || '')
    if (data.conclusion) {
      step.setAttribute('data-conclusion', data.conclusion)
      step.removeAttribute('data-in-progress')
    } else if (data.started_at) {
      step.setAttribute('data-in-progress', '')
    }
    if (data.log_url) {
      step.setAttribute('data-log-url', data.log_url)
    }
    if (data.is_background) {
      step.setAttribute('data-background', '')
    }
    if (data.background_control_type) {
      step.setAttribute('data-step-type', data.background_control_type)
    }
    if (this.shouldAutoExpandInlineDetails(data)) {
      step.setAttribute('data-expand', 'true')
    }
    if (data.background_control_step_ids?.length) {
      step.setAttribute('data-wait-step-ids', data.background_control_step_ids.join(','))
    }
    if (data.parallel_group_id) {
      step.setAttribute('data-parallel-group-id', data.parallel_group_id)
    }
    if (this.useActionsUx) {
      step.setAttribute('data-use-actions-ux', '')
    }
    if (this.loggedIn) {
      step.setAttribute('data-logged-in', '')
    }
    if (this.isJobCompleted()) {
      step.setAttribute('data-job-completed', '')
    }
  }

  async fetchInitialBackscrolls() {
    const inProgressSteps = [...this.querySelectorAll<CheckStepElement>('check-step[data-in-progress]')]

    await Promise.allSettled(
      inProgressSteps.map(async checkStep => {
        const stepId = checkStep.getAttribute('data-external-id')
        const jobStepBackscrollURL = checkStep.getAttribute('data-job-step-backscroll-url')
        if (!stepId || !jobStepBackscrollURL) return

        const backscrolls = await fetchJson<StepBackscrollLogs>(jobStepBackscrollURL)
        this.#aliveBackscrollBuffer[stepId] ||= []
        for (const line of backscrolls?.lines || []) {
          if (this.#logIds.has(line.id)) {
            continue
          }

          this.#aliveBackscrollBuffer[stepId]?.push(line)
        }
      }),
    )
  }

  flushBufferedLogs() {
    for (const [stepId, lines] of Object.entries(this.#aliveBackscrollBuffer)) {
      const step = stepsMap[stepId]
      if (!step || !lines.length) {
        continue
      }

      this.renderAliveBufferAndBackscrolls(stepId, lines)
      delete this.#aliveBackscrollBuffer[stepId]
    }
  }

  renderAliveBufferAndBackscrolls(stepId: string, lines: LogLine[]) {
    if (!lines || lines.length === 0) return

    const step = stepsMap[stepId]
    if (!step) return

    // Sort lines by group and line number (if present). IDs are in the form of a string like "1-1". The first number is the group ID, the second is the line ID.
    // Split the string and then parseInt to get the numbers. If the line ID is not present, default to 0.
    const sortedLines = lines.sort((a, b) => {
      const lineIDsA = a.id.split('-')
      const lineIDsB = b.id.split('-')

      const aGroupID = parseInt(lineIDsA[0] || '0')
      const bGroupID = parseInt(lineIDsB[0] || '0')

      if (aGroupID === bGroupID) {
        const aLineID = parseInt(lineIDsA[1] || '0')
        const bLineID = parseInt(lineIDsB[1] || '0')

        return aLineID > bLineID ? 1 : -1
      }

      return aGroupID > bGroupID ? 1 : -1
    })
    const firstId = sortedLines[0]?.id?.split('-')
    const start = parseInt(firstId?.[1] || '0')

    updateStreamingLogs(
      step,
      sortedLines.map(l => ({
        text: l.line,
        ts: tsFromID(l.id),
      })),
      start,
    )
  }

  isJobCompleted() {
    return this.jobStatus === 'completed'
  }

  areStepsCompleted() {
    return [...this.querySelectorAll('check-step')].filter(step => step.hasAttribute('data-in-progress')).length === 0
  }

  hasSteps() {
    return [...this.querySelectorAll('check-step')].length > 0
  }

  async waitAndIncrementRetries(): Promise<boolean> {
    this.#requestInProgressOrWaiting = true
    // if the job is completed and all of the steps are finished, stop retrying to fetch
    const allCompleted = this.isJobCompleted() && this.areStepsCompleted()
    if (this.#retries === MAX_RETRIES || allCompleted) {
      this.#retries = 0
      this.#requestInProgressOrWaiting = false
      return false
    }
    this.#retries++
    await new Promise(res => setTimeout(res, RETRY_DELAY))
    this.#requestInProgressOrWaiting = false
    return true
  }
}
